import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '~/context/AuthProvider';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import CampaignAccordionItem from './CampaignAccordionItem';
import CampaignApplicationAccordionItem from './CampaignApplicationAccordionItem';
import CampaignRequestAccordionItem from './CampaignRequestAccordionItem';
import FundRequestAccordionItem from './FundRequestAccordionItem';
import StatusDurationAccordionItem from './StatusDurationAccordionItem';
import TeamMemberAccordionItem from './TeamMemberAccordionItem';
import MonthlyCampaignProfilesAccordionItem from './predefined/MonthlyCampaignProfilesAccordionItem';
import MedProsNeedingEmailAccordionItem from './predefined/MedProsNeedingEmailAccordionItem';
import TotalFundsRaisedByMedCenterAccordionItem from './predefined/TotalFundsRaisedByMedCenterAccordionItem';
import MonthlyCRStatsAccordionItem from './predefined/MonthlyCRStatsAccordionItem';
import CampaignRequestTimelineAccordionItem from './predefined/CampaignRequestTimelineAccordionItem';
import CampaignOpenToDisbursableAccordionItem from './predefined/CampaignOpenToDisbursableAccordionItem';
import CampaignLastActivityAccordionItem from './predefined/CampaignLastActivityAccordionItem';
import RefundAuditAccordionItem from './predefined/RefundAuditAccordionItem';

// Param keys per accordion
const campaignParams = ['ntaf_id', 'campaign_status', 'campaign_created_start_date',
  'campaign_created_end_date', 'campaign_funded_start_date', 'campaign_funded_end_date',
  'privacy', 'state', 'zip_code', 'special_circumstance', 'living_donor', 'deceased',
  'funds_raised_start_date', 'funds_raised_end_date', 'funds_raised_min', 'funds_raised_max',
  'unique_donors_min', 'unique_donors_max'];
const caParams = ['contact_name', 'client_name', 'requirements_complete', 'community',
  'insurance', 'csc', 'pc_relationship', 'referral_source', 'referred_by',
  'created_by', 'prescreen_result', 'photo_consented'];
const crParams = ['status', 'start_date', 'end_date', 'name', 'approve_start_date', 'approve_end_date'];
const frParams = ['ap_invoice_number', 'payee', 'vendor_id', 'expense_type',
  'source', 'amount_min', 'amount_max', 'created_date_start', 'created_date_end',
  'updated_date_start', 'updated_date_end'];
const sdParams = ['submitted_date_start', 'submitted_date_end'];
// Team Member (HHLTB-2800). Only params this accordion alone owns -- it shares
// campaign_status, region, state and csc with the Campaign accordion, and
// claiming those would steal a Campaign search on the way back.
const tmParams = ['role', 'relationship', 'email_provided', 'patient_state',
  'patient_zip_code', 'medical_issue_start_date', 'medical_issue_end_date',
  'listing_start_date', 'listing_end_date'];

const detectActiveAccordion = (params: URLSearchParams): number | null => {
  if (tmParams.some(p => params.has(p))) return 6;
  if (frParams.some(p => params.has(p))) return 4;
  if (crParams.some(p => params.has(p))) return 3;
  if (caParams.some(p => params.has(p))) return 2;
  if (campaignParams.some(p => params.has(p))) return 1;
  if (sdParams.some(p => params.has(p))) return 5;
  return null;
};

type View = 'advanced' | 'predefined';

const SearchFiltersAccordion: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { filterParams, activeAccordion } = useReportFilters();
  const { user } = useAuth();

  // Mirrors FinanceAdminPermission on the refund-audit endpoint — that report
  // pairs donor PII with dollar amounts, unlike the other predefined reports.
  const hasFinanceAccess =
    !!user?.roles?.includes('administrator') || !!user?.roles?.includes('Financial');

  const initialView: View = searchParams.get('view') === 'predefined' ? 'predefined' : 'advanced';
  const [view, setView] = useState<View>(initialView);

  const switchView = (next: View) => {
    setView(next);
    const sp = new URLSearchParams(searchParams);
    if (next === 'advanced') {
      sp.delete('view');
    } else {
      sp.set('view', next);
    }
    setSearchParams(sp, { replace: true });
  };

  // Determine initial active from context, URL params, or filter params
  const getInitialActive = (): number | null => {
    if (activeAccordion !== null) return activeAccordion;
    const fromUrl = detectActiveAccordion(searchParams);
    if (fromUrl !== null) return fromUrl;
    if (filterParams) return detectActiveAccordion(new URLSearchParams(filterParams));
    return null;
  };

  const [active, setActive] = useState<number | null>(getInitialActive);

  // Restore filters from context if URL has none
  useEffect(() => {
    if (searchParams.toString() === '' && filterParams) {
      setSearchParams(new URLSearchParams(filterParams), { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (index: number) => {
    if (active === index) {
      setActive(null);
    } else {
      setActive(index);
    }
  };

  const tabClass = (active: boolean) =>
    `px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-primary text-primary'
        : 'border-transparent text-body hover:text-black'
    }`;

  return (
    <div className='p-4 sm:p-6 xl:p-12.5'>
      <div className='mx-auto mb-8 w-full max-w-[932px]'>
        <div className='flex border-b border-stroke'>
          <button type='button' className={tabClass(view === 'advanced')} onClick={() => switchView('advanced')}>
            Advanced Searches
          </button>
          <button type='button' className={tabClass(view === 'predefined')} onClick={() => switchView('predefined')}>
            Predefined Reports
          </button>
        </div>
      </div>

      {view === 'advanced' && (
        <div className='flex flex-col items-center gap-7.5'>
          <CampaignAccordionItem key={1} id={1} active={active} handleToggle={handleToggle} />
          <CampaignApplicationAccordionItem key={2} id={2} active={active} handleToggle={handleToggle} />
          <CampaignRequestAccordionItem key={3} id={3} active={active} handleToggle={handleToggle} />
          <FundRequestAccordionItem key={4} id={4} active={active} handleToggle={handleToggle} />
          <StatusDurationAccordionItem key={5} id={5} active={active} handleToggle={handleToggle} />
          <TeamMemberAccordionItem key={6} id={6} active={active} handleToggle={handleToggle} />
        </div>
      )}

      {view === 'predefined' && (
        <div className='mx-auto grid w-full max-w-[932px] grid-cols-1 items-stretch gap-6 md:grid-cols-2'>
          <MonthlyCampaignProfilesAccordionItem />
          <MedProsNeedingEmailAccordionItem />
          <TotalFundsRaisedByMedCenterAccordionItem />
          <MonthlyCRStatsAccordionItem />
          <CampaignRequestTimelineAccordionItem />
          <CampaignOpenToDisbursableAccordionItem />
          <CampaignLastActivityAccordionItem />
          {hasFinanceAccess && <RefundAuditAccordionItem />}
        </div>
      )}
    </div>
  );
};

export default SearchFiltersAccordion;
